import Cookie from 'js-cookie'

const state = () => ({
  loading: false,
  poster: {
    source: '',
    type: 'image'
  },
  type: 'Streaming Software',
  token: null,
  isLive: false,
  isOpen: false,
  stream_id: null,
  rtmp_server_stream_key: null,
  server: null,
  health: false,
  roomID: null,
  selectedVideoDevice: '',
  selectedAudioDevice: '',
  stream: null,
  preview: true,
  layout: 'layout1',
  needToLinkRTMP: true
})

const resolveLiveRecordId = (state) => {
  return state.stream_id || state.rtmp_server_stream_key || null
}

const getProjectId = (rootState) => {
  const projectId = rootState?.projects?.project?.id

  if (projectId === undefined || projectId === null || projectId === '') {
    throw new Error('Project id is not available')
  }

  return String(projectId)
}

const normalizeOrigin = (value = '') => {
  return String(value || '').trim().replace(/\/+$/, '')
}

const isLocalHost = (value = '') => {
  const normalized = String(value || '').toLowerCase()
  return normalized.includes('localhost') || normalized.includes('127.0.0.1') || normalized.includes('0.0.0.0')
}

const normalizeHostConfig = (value = '') => {
  const normalized = normalizeOrigin(value)

  if (!normalized) {
    return {
      protocol: 'https',
      host: 'estage.site'
    }
  }

  if (/^https?:\/\//i.test(normalized)) {
    const parsed = new URL(normalized)
    return {
      protocol: isLocalHost(parsed.host) ? parsed.protocol.replace(':', '') : 'https',
      host: parsed.host
    }
  }

  return {
    protocol: isLocalHost(normalized) ? 'http' : 'https',
    host: normalized
  }
}

const getPublicApiBase = (rootState) => {
  const project = rootState?.projects?.project || {}
  const subdomain = project?.subdomain
  const customDomain = normalizeOrigin(project?.domain || '').replace(/^https?:\/\//i, '')

  if (process.env.NODE_ENV === 'development' && subdomain) {
    return `http://${subdomain}.estage.localhost:3000`
  }

  if (customDomain) {
    return `https://${customDomain}`
  }

  if (!subdomain) {
    throw new Error('Project public subdomain is not available')
  }

  const { protocol, host } = normalizeHostConfig(process.env.VUE_APP_ESTAGE_SITE_HOST)

  return `${protocol}://${subdomain}.${host}`
}

const getAuthToken = () => {
  const localToken = typeof window !== 'undefined' ? localStorage.getItem('sanctum_token') : ''
  const cookieToken = Cookie.get('cross_estage_token') || ''

  if (process.env.NODE_ENV === 'development') {
    return localToken || cookieToken || ''
  }

  return cookieToken || localToken || ''
}

const getAuthHeaders = () => {
  const token = getAuthToken()

  return {
    'Accept': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(token ? { 'Estage-Authorization': token } : {})
  }
}

const getJsonAuthHeaders = () => {
  return {
    ...getAuthHeaders(),
    'Content-Type': 'application/json'
  }
}

const withLiveAuthToken = (url) => {
  const token = getAuthToken()

  if (!token || process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return url
  }

  const normalizedUrl = new URL(url, window.location.origin)
  normalizedUrl.searchParams.set('auth_token', token)

  return normalizedUrl.toString()
}

const readJsonResponse = async (response, fallbackMessage) => {
  const contentType = response.headers.get('content-type') || ''
  let payload = null

  if (contentType.includes('application/json')) {
    payload = await response.json().catch(() => null)
  } else {
    const text = await response.text().catch(() => '')
    payload = text ? { message: text } : null
  }

  if (!response.ok) {
    throw new Error(payload?.message || fallbackMessage || `Request failed with ${response.status}`)
  }

  return payload || {}
}

const actions = {
  async rotateServers () {
    let servers = [
      'https://estagestream.com',
      'https://edge1.estagelive.com',
      'https://edge2.estagelive.com'
    ]

    const selectedServer = servers.shift()
    servers.push(selectedServer)
    return selectedServer
  },
  async getServer ({state}) {
    const response = await fetch('https://estagesocial.com/api/stream/server', {
      headers: {
        'Accept': 'application/json'
      }
    })

    const payload = await readJsonResponse(response, 'Unable to fetch stream server')
    state.server = payload
    return payload
  },
  async getLiveStreamToken ({state}) {
    const token = state.stream_id || state.rtmp_server_stream_key || null
    state.token = token
    return {token}
  },
  async postLiveStream ({state, rootState}, payload = {}) {
    const projectId = getProjectId(rootState)
    const url = withLiveAuthToken(`${getPublicApiBase(rootState)}/api/live/${projectId}/public`)

    const response = await fetch(url, {
      method: 'POST',
      headers: getJsonAuthHeaders(),
      body: JSON.stringify(payload || {})
    })

    const data = await readJsonResponse(response, 'Unable to create live stream')

    state.stream_id = data.id || state.stream_id
    state.rtmp_server_stream_key = data?.awsChannelData?.streamKey || state.rtmp_server_stream_key
    state.token = data.token || data.id || state.rtmp_server_stream_key || null

    return data
  },
  async linkRTMP ({rootState}, id) {
    const projectId = getProjectId(rootState)
    const url = withLiveAuthToken(`${getPublicApiBase(rootState)}/api/live/${projectId}/${id}/connect`)
    const response = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders()
    })

    return readJsonResponse(response, 'Unable to link RTMP stream')
  },
  async postLiveStreamTest ({state, rootState}, payload) {
    const data = await actions.postLiveStream({state, rootState}, payload)

    if (payload && data?.awsChannelData?.streamKey) {
      payload.rtmp_server_stream_key = data.awsChannelData.streamKey
    }

    return data
  },
  async postLiveStreamTestAxios ({state, rootState}, payload) {
    const data = await actions.postLiveStream({state, rootState}, payload)

    if (payload && data?.awsChannelData?.streamKey) {
      payload.rtmp_server_stream_key = data.awsChannelData.streamKey
    }

    return {data}
  },
  async putLiveStream ({state, rootState}, desiredIsOpen = null) {
    let liveRecordId = resolveLiveRecordId(state)

    if (!liveRecordId) {
      throw new Error('Live stream id is not available')
    }

    const nextIsOpen = typeof desiredIsOpen === 'boolean' ? desiredIsOpen : !state.isOpen
    const projectId = getProjectId(rootState)
    const url = withLiveAuthToken(
      `${getPublicApiBase(rootState)}/api/live/hls/${liveRecordId}?is_open=${nextIsOpen}&project_id=${projectId}`
    )
    const response = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders()
    })

    if (response.status === 404) {
      await actions.postLiveStream({state, rootState})
      liveRecordId = resolveLiveRecordId(state)

      if (!liveRecordId) {
        throw new Error('Failed to create a new live stream record')
      }

      const retryUrl = withLiveAuthToken(
        `${getPublicApiBase(rootState)}/api/live/hls/${liveRecordId}?is_open=${nextIsOpen}&project_id=${projectId}`
      )
      const retryResponse = await fetch(retryUrl, {
        method: 'PUT',
        headers: getAuthHeaders()
      })

      const data = await readJsonResponse(retryResponse, 'Unable to update live stream state')

      state.isOpen = Boolean(data?.isOpen)
      state.isLive = Boolean(data?.streamLive || data?.isOpen)

      return data
    }

    const data = await readJsonResponse(response, 'Unable to update live stream state')

    state.isOpen = Boolean(data?.isOpen)
    state.isLive = Boolean(data?.streamLive || data?.isOpen)

    return data
  },
  async putLiveStreamPreview ({state, rootState}, payload) {
    const liveRecordId = resolveLiveRecordId(state)
    if (!liveRecordId) {
      throw new Error('Live stream id is not available')
    }

    const projectId = getProjectId(rootState)
    const url = withLiveAuthToken(`${getPublicApiBase(rootState)}/api/live/${projectId}/${liveRecordId}/poster`)

    const body = payload

    await fetch(url, {
      method: 'PUT',
      headers: getJsonAuthHeaders(),
      body: JSON.stringify(body)
    })

    state.poster.source = body.source
    state.poster.type = body.type
  },
  async putLiveStreamData ({state, rootState}, payload) {
    const liveRecordId = resolveLiveRecordId(state)
    if (!liveRecordId) {
      throw new Error('Live stream id is not available')
    }

    const projectId = getProjectId(rootState)
    const url = withLiveAuthToken(`${getPublicApiBase(rootState)}/api/live/${projectId}/${liveRecordId}/stream`)

    const response = await fetch(url, {
      method: 'PUT',
      headers: getJsonAuthHeaders(),
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `Live stream status update failed with ${response.status}`)
    }

    return response
  }
}

const mutations = {
  SET_STREAM_ID (state, payload) {
    state.stream_id = payload
  },
  SET_ROOM_ID (state, payload) {
    state.roomID = payload
  },
  SET_POSTER (state, payload) {
    state.poster = payload
  },
  SET_HEALTH (state, payload) {
    state.health = payload
  },
  SET_LOADING (state, payload) {
    state.loading = payload
  },
  SET_LIVE (state, payload) {
    state.isLive = payload
  },
  SET_OPEN (state, payload) {
    state.isOpen = payload
  },
  SET_RTMP_SERVER_STREAM_KEY (state, payload) {
    state.rtmp_server_stream_key = payload
  },
  SET_SELECTED_VIDEO_DEVICE (state, payload) {
    state.selectedVideoDevice = payload
  },
  SET_SELECTED_AUDIO_DEVICE (state, payload) {
    state.selectedAudioDevice = payload
  },
  SET_STREAM (state, payload) {
    state.stream = payload
  },
  SET_PREVIEW (state, payload) {
    state.preview = payload
  },
  SET_LAYOUT (state, payload) {
    state.layout = payload
  },
  SET_NEED_TO_LINK_RTMP (state, payload) {
    state.needToLinkRTMP = payload
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations
}
